import React, { useState, useMemo, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useFilters } from '../../../contexts/FiltersContext';
import { formatTransportMode } from '../../../utils/transportModeDisplay';
import { buildPagedScope, usePagedOptions, type SearchDimension } from './usePagedOptions';
import {
  Box,
  Typography,
  Checkbox,
  Radio,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Dimensions too large for the hierarchy endpoint: their options are cursor-paged
// from /filter/search (see usePagedOptions) instead of arriving as a full list.
export type { SearchDimension };

export interface PanelValue {
  id: string;
  value: string;
  isChecked?: boolean;
}

export interface PanelItem {
  type: 'radiolist' | 'checklist';
  key: string;
  label: string;
  values: PanelValue[];
  selected?: PanelValue;
}

interface PanelSectionProps {
  panelItem: PanelItem;
  type: 'radiolist' | 'checklist';
  handleSidePanelSelection: (key: string) => void;
  /**
   * When set, the section is paged and virtualised: options come from
   * /filter/search, not from panelItem.values. Strictly local: it never touches
   * FiltersContext beyond the user's own ticks, so scrolling or typing can never
   * reload the whole panel.
   */
  searchDimension?: SearchDimension;
}

const PAGED_NOUN: Record<SearchDimension, string> = {
  subCustomer: 'customers',
  originCity: 'cities',
  destinationCity: 'cities',
};

const LIST_HEIGHT = 240;

const scrollbarSx = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: '#f4f6f8', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': { background: '#90bf45', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#557010' },
};

const searchFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.85rem',
    bgcolor: '#f9fafb',
    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(144, 191, 69, 0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#90bf45', borderWidth: '1.5px' },
    '& input': { padding: '8px 8px 8px 0', fontSize: '0.85rem' },
  },
};

const PanelSection: React.FC<PanelSectionProps> = ({
  panelItem,
  type,
  handleSidePanelSelection,
  searchDimension,
}) => {
  const useFilterContext = useFilters();
  const [searchQuery, setSearchQuery] = useState('');
  const isPaged = Boolean(searchDimension);
  // Paged sections start collapsed and fetch their first page on first expand.
  const [expanded, setExpanded] = useState(!isPaged);
  const [everExpanded, setEverExpanded] = useState(!isPaged);

  const isTransportMode = panelItem.key === 'transportMode';
  const displayValue = useCallback(
    (val: string) => (isTransportMode ? formatTransportMode(val) : val),
    [isTransportMode]
  );

  // Scope excludes this list's own selection, so ticking a value never resets it.
  const scopeParams = searchDimension ? buildPagedScope(useFilterContext, searchDimension) : {};
  const paged = usePagedOptions({
    dimension: searchDimension,
    scopeParams,
    term: searchQuery,
    enabled: isPaged && everExpanded,
  });

  // Current selections of a paged dimension, straight from context. They carry
  // their label, so they stay visible even when no loaded page contains them.
  const selectedForDim = useMemo<PanelValue[]>(() => {
    switch (searchDimension) {
      case 'subCustomer':
        return useFilterContext.subCustomerData as PanelValue[];
      case 'originCity':
        return useFilterContext.cityOriginData as PanelValue[];
      case 'destinationCity':
        return useFilterContext.cityDestinationData as PanelValue[];
      default:
        return [];
    }
  }, [searchDimension, useFilterContext.subCustomerData, useFilterContext.cityOriginData, useFilterContext.cityDestinationData]);

  // Paged list = selections first, then the server pages minus those ids. While a
  // term is active only selections that match it are pinned (the rest stay counted
  // in the badge and reappear when the term is cleared).
  const pagedValues = useMemo<PanelValue[]>(() => {
    if (!isPaged) return [];
    const term = searchQuery.trim().toLowerCase();
    const pinned = term
      ? selectedForDim.filter(
          (v) => v.value.toLowerCase().includes(term) || v.id.toLowerCase().includes(term)
        )
      : selectedForDim;
    const selectedIds = new Set(selectedForDim.map((v) => v.id));
    return [
      ...pinned.map((v) => ({ id: v.id, value: v.value })),
      ...paged.items.filter((v) => !selectedIds.has(v.id)),
    ];
  }, [isPaged, searchQuery, selectedForDim, paged.items]);

  const toFilterItem = useCallback(
    (item: PanelValue) => ({ type: 'Filter', key: panelItem.key, value: item.value, id: item.id }),
    [panelItem.key]
  );

  // Non-paged lists: search term filter, then checked items first, then A-Z.
  const filteredValues = useMemo(() => {
    if (isPaged) return [];
    let filtered = panelItem.values;

    if (searchQuery.trim()) {
      filtered = filtered.filter((item) =>
        item.value.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Resolve checked state once per item, not twice per comparison.
    const checked = new Map<string, boolean>();
    for (const item of filtered) {
      checked.set(
        item.id,
        type === 'checklist'
          ? useFilterContext.isPresent(toFilterItem(item), panelItem.key)
          : panelItem.selected?.id === item.id
      );
    }

    return [...filtered].sort((a, b) => {
      const aSel = checked.get(a.id) ?? false;
      const bSel = checked.get(b.id) ?? false;
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return a.value.localeCompare(b.value);
    });
  }, [isPaged, panelItem.values, panelItem.key, panelItem.selected, searchQuery, type, useFilterContext, toFilterItem]);

  const handleCheckboxChange = (item: PanelValue, checked: boolean) => {
    const filterItem = toFilterItem(item);

    if (checked) {
      useFilterContext.addFilter(filterItem, 'Filter', panelItem.key);
    } else {
      useFilterContext.removeFilter(filterItem, panelItem.key);
    }

    handleSidePanelSelection(panelItem.key);
  };

  const handleRadioChange = (item: PanelValue) => {
    useFilterContext.handleRadioSelect(toFilterItem(item), 'Filter', panelItem.key);
    handleSidePanelSelection(panelItem.key);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling

    // Clear all items at once for this filter type
    useFilterContext.clearAllType(panelItem.key);

    handleSidePanelSelection(panelItem.key);
  };

  // Count selected items
  const selectedCount = useMemo(() => {
    if (isPaged) return selectedForDim.length;
    if (type === 'checklist') {
      return panelItem.values.filter((item) =>
        useFilterContext.isPresent(toFilterItem(item), panelItem.key)
      ).length;
    } else {
      return panelItem.selected ? 1 : 0;
    }
  }, [isPaged, selectedForDim, type, panelItem.values, panelItem.key, panelItem.selected, useFilterContext, toFilterItem]);

  const wrapLabels = searchDimension === 'subCustomer';

  // Footer of a paged list: error, else "<total> customers" (or "matching" while
  // searching), plus a hint to type while more pages exist.
  const footerText = (() => {
    if (!searchDimension) return '';
    if (paged.error) return paged.error;
    const parts: string[] = [];
    if (paged.total !== null) {
      const noun = searchQuery.trim() ? 'matching' : PAGED_NOUN[searchDimension];
      parts.push(`${paged.total.toLocaleString('en-US')} ${noun}`);
    }
    if (paged.hasMore) parts.push('type to search');
    return parts.join(' · ');
  })();

  const rowSx = {
    width: '100%',
    ml: 0,
    mr: 0,
    mb: 0.5,
    py: 0.5,
    px: 1.5,
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      bgcolor: 'rgba(144, 191, 69, 0.04)',
    },
  };

  const renderLabel = (item: PanelValue, active: boolean) => (
    <Typography
      variant="body2"
      sx={{
        fontSize: '0.85rem',
        fontWeight: active ? 500 : 400,
        color: active ? '#333' : '#666',
        transition: 'all 0.2s ease-in-out',
        width: '100%',
        // Sub-customer names are shown exactly as stored: pre-wrap keeps repeated
        // spaces ("A  A") that HTML would collapse, and still wraps long names.
        ...(wrapLabels ? { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } : {}),
      }}
    >
      {displayValue(item.value)}
    </Typography>
  );

  const renderChecklistRow = (item: PanelValue) => {
    const isChecked = useFilterContext.isPresent(toFilterItem(item), panelItem.key);
    return (
      <FormControlLabel
        key={item.id}
        control={
          <Checkbox
            checked={isChecked}
            onChange={(e) => handleCheckboxChange(item, e.target.checked)}
            size="small"
            sx={{
              padding: 0,
              marginRight: '8px',
              alignSelf: 'flex-start',
              color: 'rgba(0, 0, 0, 0.3)',
              '&.Mui-checked': {
                color: '#90bf45',
              },
              '&:hover': {
                bgcolor: 'rgba(144, 191, 69, 0.08)',
              },
            }}
          />
        }
        label={renderLabel(item, isChecked)}
        sx={rowSx}
      />
    );
  };

  const renderRadioRow = (item: PanelValue) => {
    const isSelected = panelItem.selected?.id === item.id;
    return (
      <FormControlLabel
        key={item.id}
        control={
          <Radio
            checked={isSelected}
            onChange={() => handleRadioChange(item)}
            size="small"
            sx={{
              color: 'rgba(0, 0, 0, 0.3)',
              '&.Mui-checked': {
                color: '#90bf45',
              },
              '&:hover': {
                bgcolor: 'rgba(144, 191, 69, 0.08)',
              },
            }}
          />
        }
        label={renderLabel(item, isSelected)}
        sx={rowSx}
      />
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, isExpanded) => {
        setExpanded(isExpanded);
        if (isExpanded) setEverExpanded(true);
      }}
      TransitionProps={{ unmountOnExit: isPaged }}
      sx={{
        boxShadow: 'none',
        mb: 1.5,
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: '12px !important',
        overflow: 'hidden',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0 0 12px 0',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#90bf45' }} />}
        sx={{
          bgcolor: 'rgba(144, 191, 69, 0.04)',
          borderRadius: '12px',
          minHeight: 48,
          px: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            bgcolor: 'rgba(144, 191, 69, 0.08)',
          },
          '&.Mui-expanded': {
            minHeight: 48,
            borderRadius: '12px 12px 0 0',
          },
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            '&.Mui-expanded': {
              margin: '12px 0',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#333',
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
            }}
          >
            {panelItem.label}
          </Typography>
          {selectedCount > 0 && (
            <Box
              sx={{
                bgcolor: '#90bf45',
                color: 'white',
                borderRadius: '12px',
                px: 1,
                py: 0.25,
                fontSize: '0.7rem',
                fontWeight: 600,
                minWidth: '20px',
                textAlign: 'center',
              }}
            >
              {selectedCount}
            </Box>
          )}
        </Box>
        {selectedCount > 0 && (
          <Tooltip title="Clear all" placement="top">
            <IconButton
              size="small"
              onClick={handleClearAll}
              sx={{
                color: '#d32f2f',
                padding: '4px',
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1, px: 1 }}>
        {/* Search input: outside the scrolling list so it is always visible */}
        <Box sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${panelItem.label}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            inputProps={{ 'aria-label': `Search ${panelItem.label}` }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1.1rem', color: 'rgba(0, 0, 0, 0.4)' }} />
                </InputAdornment>
              ),
              endAdornment:
                isPaged && paged.loading && paged.items.length === 0 ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} sx={{ color: '#90bf45' }} />
                  </InputAdornment>
                ) : undefined,
            }}
            sx={searchFieldSx}
          />
        </Box>

        {isPaged ? (
          <Box>
            {pagedValues.length > 0 ? (
              <Box sx={{ height: LIST_HEIGHT, ...scrollbarSx }}>
                <Virtuoso
                  style={{ height: LIST_HEIGHT }}
                  data={pagedValues}
                  computeItemKey={(_index, item) => item.id}
                  endReached={paged.loadMore}
                  itemContent={(_index, item) => (
                    <Box sx={{ pb: 0.5 }}>{renderChecklistRow(item)}</Box>
                  )}
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 2, px: 1.5, fontSize: '0.85rem' }}
              >
                {paged.loading
                  ? 'Loading...'
                  : searchQuery.trim()
                  ? 'No matches found'
                  : 'No options available'}
              </Typography>
            )}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 0.5, minHeight: 20 }}
            >
              {paged.loading && paged.items.length > 0 && (
                <CircularProgress size={12} sx={{ color: '#90bf45' }} aria-label="Loading more" />
              )}
              <Typography
                variant="caption"
                color={paged.error ? 'error' : 'text.secondary'}
              >
                {footerText}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ maxHeight: LIST_HEIGHT, overflowY: 'auto', overflowX: 'hidden', ...scrollbarSx }}>
            {filteredValues.length > 0 ? (
              filteredValues.map((item) =>
                type === 'checklist' ? (
                  <React.Fragment key={item.id}>{renderChecklistRow(item)}</React.Fragment>
                ) : (
                  renderRadioRow(item)
                )
              )
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 2, px: 1.5, fontSize: '0.85rem' }}
              >
                {searchQuery ? 'No matches found' : 'No options available'}
              </Typography>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default PanelSection;
